/**
 * Shared small components and constants used across multiple editor-core views.
 */

import type { PluginDefinition } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import type { ActionDeclaration } from "@magic-md/plugin-sdk";
import {
    Check,
    CheckCircle2,
    Code2,
    Eye,
    RotateCcw,
    XCircle,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { cn } from "../lib/utils";
import type { ExecutionStatus } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

// ── Stable empty map (referential equality for selector memoisation) ──

export const EMPTY_DEFINITIONS = new Map<string, PluginDefinition>();

// ── Execution StatusDot ──

export function StatusDot({ status }: { status: ExecutionStatus }) {
    switch (status) {
        case "running":
            return (
                <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                    <span
                        className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full theme-accent-fill opacity-75"
                        style={getThemeAccentStyle("running")}
                    />
                    <span
                        className="relative inline-flex h-2 w-2 rounded-full theme-accent-fill"
                        style={getThemeAccentStyle("running")}
                    />
                </span>
            );
        case "success":
            return (
                <CheckCircle2
                    size={14}
                    className="theme-accent-text"
                    style={getThemeAccentStyle("success")}
                />
            );
        case "error":
            return (
                <XCircle
                    size={14}
                    className="theme-accent-text"
                    style={getThemeAccentStyle("error")}
                />
            );
        default:
            return <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />;
    }
}

// ── Raw JSON config editor fallback ──

export function RawConfigEditor({
    config,
    onChange,
    textareaClassName,
}: {
    config: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
    /** Override textarea height, e.g. "h-60". Default: "h-40". */
    textareaClassName?: string;
}) {
    const [text, setText] = useState(JSON.stringify(config, null, 2));
    const [error, setError] = useState<string | null>(null);

    // Sync when config changes externally
    useEffect(() => {
        setText(JSON.stringify(config, null, 2));
        setError(null);
    }, [config]);

    const handleChange = useCallback(
        (val: string) => {
            setText(val);
            try {
                const parsed = JSON.parse(val);
                setError(null);
                onChange(parsed);
            } catch {
                setError("Invalid JSON");
            }
        },
        [onChange],
    );

    return (
        <div className="space-y-1">
            <textarea
                className={cn(
                    "w-full rounded-md border bg-background px-3 py-2 text-[11px] font-mono resize-y",
                    textareaClassName ?? "h-40",
                    error ? "border-destructive" : "border-border",
                )}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                spellCheck={false}
            />
            {error && <p className="text-[10px] text-destructive">{error}</p>}
        </div>
    );
}

export function DefinitionPanelTabs({
    activeTab,
    onChange,
    outputDuration,
    compact,
    configAccessory,
}: {
    activeTab: "config" | "output";
    onChange: (tab: "config" | "output") => void;
    outputDuration?: number;
    compact?: boolean;
    configAccessory?: ReactNode;
}) {
    const t = useT("editor");
    const outputTitle =
        outputDuration != null
            ? t("definitionPanel.outputDuration", { duration: outputDuration })
            : undefined;
    const configIsActive = activeTab === "config";

    return (
        <div className="flex h-6 shrink-0 items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
            <div
                className={cn(
                    "flex h-5 items-center rounded transition-colors",
                    configIsActive
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                )}
            >
                <button
                    type="button"
                    className={cn(
                        "flex h-full items-center font-medium leading-none transition-colors",
                        compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]",
                    )}
                    onClick={() => onChange("config")}
                >
                    {t("definitionPanel.config")}
                </button>
                {configIsActive && configAccessory ? (
                    <>
                        <span className="h-3 w-px bg-border/70" />
                        <div className="pr-0.5">{configAccessory}</div>
                    </>
                ) : null}
            </div>
            <button
                type="button"
                className={cn(
                    "flex h-5 items-center rounded font-medium leading-none transition-colors",
                    compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]",
                    activeTab === "output"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => onChange("output")}
                title={outputTitle}
            >
                {t("definitionPanel.output")}
            </button>
        </div>
    );
}

export function ConfigEditorModeButton({
    useRawEditor,
    onChange,
    className,
    compact,
    embedded,
}: {
    useRawEditor: boolean;
    onChange: (value: boolean) => void;
    className?: string;
    compact?: boolean;
    embedded?: boolean;
}) {
    const t = useT("editor");
    const nextIsRaw = !useRawEditor;

    return (
        <button
            type="button"
            className={cn(
                "flex items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground shrink-0",
                embedded
                    ? compact
                        ? "h-4 w-4 text-[9px] hover:bg-muted/70"
                        : "h-5 w-5 text-[10px] hover:bg-muted/70"
                    : compact
                        ? "h-5 w-5 border border-border bg-muted/40 text-[9px] hover:bg-muted/70"
                        : "h-6 w-6 border border-border bg-muted/40 text-[10px] hover:bg-muted/70",
                useRawEditor &&
                !embedded &&
                "text-primary border-primary/20 bg-primary/10",
                useRawEditor && embedded && "text-primary",
                className,
            )}
            onClick={() => onChange(nextIsRaw)}
            title={t(nextIsRaw ? "definitionPanel.switchToJson" : "definitionPanel.switchToVisual")}
            aria-label={t(nextIsRaw ? "definitionPanel.switchToJson" : "definitionPanel.switchToVisual")}
        >
            {useRawEditor ? (
                <Eye size={compact ? 9 : 10} />
            ) : (
                <Code2 size={compact ? 9 : 10} />
            )}
        </button>
    );
}

export function DirtyStateBanner({
    onSave,
    onDiscard,
}: {
    onSave: () => void;
    onDiscard: () => void;
}) {
    const t = useT("editor");
    return (
        <div
            className="flex items-center justify-between rounded-md border px-2.5 py-1.5 theme-accent-banner"
            style={getThemeAccentStyle("warning")}
        >
            <span className="text-[10px] font-medium">
                {t("definitionPanel.unsavedConfig")}
            </span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium theme-accent-icon-button"
                    style={getThemeAccentStyle("success")}
                    onClick={onSave}
                    title={t("definitionPanel.saveConfigTitle")}
                >
                    <Check size={9} />
                    {t("common.save")}
                </button>
                <button
                    type="button"
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={onDiscard}
                    title={t("definitionPanel.discard")}
                >
                    <RotateCcw size={9} />
                    {t("definitionPanel.discard")}
                </button>
            </div>
        </div>
    );
}

export function SecondaryActionsBar({
    actions,
    disabled,
    onAction,
    children,
}: {
    actions: ActionDeclaration[];
    disabled?: boolean;
    onAction: (actionName: string) => void;
    children?: ReactNode;
}) {
    const t = useT("editor");
    if (actions.length === 0 && !children) return null;

    return (
        <div className="flex items-center gap-1.5 px-3 py-2 border-t border-border bg-muted/20 shrink-0">
            <span className="text-[10px] text-muted-foreground mr-1">
                {t("definitionPanel.actions")}:
            </span>
            {actions.map((action) => (
                <button
                    type="button"
                    key={action.name}
                    disabled={disabled}
                    className={cn(
                        "flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border transition-colors",
                        action.dangerous
                            ? "border-destructive/30 text-destructive hover:bg-destructive/10"
                            : "border-border text-foreground hover:bg-muted",
                    )}
                    onClick={() => onAction(action.name)}
                >
                    {action.label}
                </button>
            ))}
            {children}
        </div>
    );
}
